import { useEffect, useRef } from 'react'
import { Box, Group, Text } from '@mantine/core'
import { toast } from 'react-toastify'
import type { BuildPartDto } from '../../app/models/build'

// The 12 "core" build zones — excludes "Sling / Mount" (accessory) and "Other" (misc)
export const COMPLETION_ZONES = [
  'Muzzle Device', 'Barrel', 'Handguard / Rail', 'Bolt Carrier Group',
  'Charging Handle', 'Upper Receiver', 'Optic', 'Light / Laser',
  'Trigger Group', 'Lower Receiver', 'Pistol Grip', 'Stock / Brace'
] as const

const TOTAL = COMPLETION_ZONES.length // 12

interface Props {
  parts: BuildPartDto[];
}

export default function BuildCompletionBar ({ parts }: Props) {
  const populatedZones = new Set(parts.map((bp) => bp.zoneOverride ?? bp.part?.category ?? 'Other'))
  const populatedCount = COMPLETION_ZONES.filter((z) => populatedZones.has(z)).length
  const percentage = (populatedCount / TOTAL) * 100
  const isComplete = populatedCount >= TOTAL

  // Fire the "full build" toast exactly once per session
  const notifiedRef = useRef(false)
  useEffect(() => {
    if (isComplete && !notifiedRef.current) {
      notifiedRef.current = true
      toast.success('Full build! Certified.', { toastId: 'full-build', icon: '🎯' })
    }
  }, [isComplete])

  return (
    <Box mb={16}>
      <Group position="apart" mb={6}>
        <Text
          size="xs"
          weight={700}
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            color: 'var(--gs-muted)',
            textTransform: 'uppercase' as const,
            letterSpacing: 0.8
          }}
        >
          Build Completion
        </Text>
        <Group spacing={4} align="center">
          {isComplete && (
            <Text component="span" sx={{ color: 'var(--gs-gold)', fontSize: 13, lineHeight: 1 }}>✓</Text>
          )}
          <Text
            size="xs"
            sx={{
              fontFamily: 'ui-monospace, monospace',
              color: isComplete ? 'var(--gs-gold)' : 'var(--gs-muted)',
              fontWeight: isComplete ? 700 : 400
            }}
          >
            {isComplete ? 'Full Build' : `${populatedCount} / ${TOTAL} zones`}
          </Text>
        </Group>
      </Group>

      {/* Track */}
      <Box
        sx={{
          width: '100%',
          height: 6,
          borderRadius: 3,
          background: 'var(--gs-surface2)',
          overflow: 'hidden'
        }}
      >
        {/* Fill */}
        <Box
          sx={{
            height: '100%',
            width: `${percentage}%`,
            borderRadius: 3,
            background: isComplete
              ? 'linear-gradient(90deg, #E85D04, #C9A84C)'
              : 'var(--gs-accent)',
            transition: 'width 400ms ease'
          }}
        />
      </Box>
    </Box>
  )
}
